import { ToastrService } from 'ngx-toastr';
import { combineLatest } from 'rxjs';
import { COURSE_ACTIVITY_NAME_MAX_LENGTH, HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL } from 'src/app/constants/a48.constants';
import { A48ActivityType } from 'src/app/model/enum/a48-activity-type.enum';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { PortalHelpersService } from 'src/app/services/portal-helpers/portal-helpers.service';

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AUN_LENGTH, AUNSettings, CommonValidators, DateHelpers, PortalSettings, PPID_LENGTH, SessionService, User, UserService } from '@edl-development/edl-common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CplA48UserSubmissionDetail } from '../../model/a48/cpl-a48-user-submission-detail.model';
import { A48CourseTypes } from '../../model/enum/a48-sub-type.enum';
import { Act48Service } from '../../services/a48.service';

@Component({
    templateUrl: './a48-user-details-confirm-modal.component.html'
})
export class Act48UserSubmissionDetailsModalComponent implements OnInit {

    @Input() TypeId: number;
    @Input() Type: number;
    @Input() userId: string;
    @Input() IsFollowUp: boolean;
    @Input() AdminUI: boolean;
    @Input() ShowClose: boolean;
    @Input() isReadOnly: boolean = false;
    @Input() isItemComplete: boolean;

    @Output() onSubmissionComplete = new EventEmitter<{ success: boolean, details: CplA48UserSubmissionDetail }>();

    form: UntypedFormGroup;
    busy: boolean;
    details: CplA48UserSubmissionDetail;
    user: User;
    A48Status: number;

    IsAdminView: boolean;
    ShowCloseBtn: boolean;
    isParaApplicable: boolean;
    isPara: boolean;
    isPpidRequired: boolean = false;
    portalSettings: PortalSettings;

    // act 48
    aunSettings: AUNSettings[];
    activityTypes: any;
    courseTypes: any;
    collegeCreditTypes: any;
    courseTypeId: number;
    cecPerformanceTypes: any;
    confirmA48Edit: boolean;

    A48ActivityType = A48ActivityType;
    A48types = new A48CourseTypes();
    TimeTrackingType = TimeTrackingTypes;

    DateHelpers = DateHelpers;

    constructor(
        public activeModal: NgbActiveModal,
        public a48HelpersService: A48HelpersService,
        private portalHelpersService: PortalHelpersService,
        private formBuilder: UntypedFormBuilder,
        private act48Service: Act48Service,
        private userService: UserService,
        private sessionService: SessionService,
        private toastrService: ToastrService
    ) { }

    ngOnInit() {
        this.portalSettings = this.sessionService.PortalSettings;
        this.IsAdminView = this.AdminUI ? this.AdminUI : false;
        this.ShowCloseBtn = this.ShowClose ? this.ShowClose : false;
        if (this.IsFollowUp == null) this.IsFollowUp = false;
        this.details = new CplA48UserSubmissionDetail();
        this.busy = true;

        const { aunSettingsForItem, currentAUNSetting } = this.portalHelpersService.getAUNSettingsForItem(this.portalSettings);
        this.aunSettings = aunSettingsForItem;
        this.activityTypes = this.portalHelpersService.getValidActivityTypes(this.A48types, currentAUNSetting);


        const defaultAUN = currentAUNSetting ? currentAUNSetting.AUN : null;
        const defaultActivityType = this.activityTypes.length === 1 ? this.activityTypes[0].id : null;

        this.form = this.formBuilder.group({
            PPID: [null, [CommonValidators.isNumericCode(PPID_LENGTH)]],
            Provider: [null, []],
            Hours: [null, [Validators.required, CommonValidators.isGreaterThanZero(), CommonValidators.isValidDecimal(HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL)]],
            AUN: [defaultAUN, [Validators.required, CommonValidators.isNumericCode(AUN_LENGTH)]],
            ActivityType: [defaultActivityType, []],
            CourseType: [null, []],
            CECPerformanceStandard: [null, []],
            EventName: [null, []],
            EventStart: [null, []],
            EventEnd: [null, []],
            IsUserEventType: [0, []],
            ShouldSubmitToPerms: [true, []]
        });

        this.courseTypes = this.A48types.courseType;
        this.collegeCreditTypes = this.A48types.collegeCreditType;
        this.cecPerformanceTypes = this.A48types.cecPerformance;

        combineLatest([
            this.userService.getAccountByUserId(this.userId),
            this.act48Service.getAct48UserSubmissionDetails(this.TypeId, this.Type)
        ]).subscribe(([user, details]: [User, CplA48UserSubmissionDetail]) => {
            this.busy = false;
            this.user = user;
            this.details = details;

            this.A48Status = details.StatusID;
            this.courseTypeId = this.details && this.details.CourseType ? this.details.CourseType : 0;

            this.isParaApplicable = details.ParaApplicable == 1;
            this.isPara = details.IsPara;

            const { aunSettingsForItem, currentAUNSetting } = this.portalHelpersService
                .getAUNSettingsForItem(this.portalSettings, details?.AUN, details?.ActivityType);
            this.aunSettings = aunSettingsForItem;
            this.activityTypes = this.portalHelpersService.getValidActivityTypes(this.A48types, currentAUNSetting, details?.ActivityType);

            this.buildForm();
        }, error => {
            this.busy = false;
        });

        this.form.get('AUN').valueChanges.subscribe(() => {
            this.onAUNChange();
        });

        if (this.isReadOnly) this.form.disable();
    }

    buildForm() {
        this.form = this.formBuilder.group({
            IsFollowup: [this.IsFollowUp, [Validators.required]],
            SubmissionID: [this.details.SubmissionID, [Validators.required]],
            UserId: [this.details.UserId, [Validators.required]],
            TypeId: [this.details.TypeId, [Validators.required]],
            Type: [this.details.Type, [Validators.required]],
            PPID: [this.details.PPID, this.details.IsPara ? [CommonValidators.isNumericCode(PPID_LENGTH)] : [Validators.required, CommonValidators.isNumericCode(PPID_LENGTH)]],
            Provider: [this.details.Provider, this.details.IsPara ? [] : [Validators.required]],
            Hours: [this.details.Hours == 0 ? null : this.details.Hours, [Validators.required, CommonValidators.isGreaterThanZero(), CommonValidators.isValidDecimal(HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL)]],
            AUN: [this.details.AUN, [Validators.required, CommonValidators.isNumericCode(AUN_LENGTH)]],
            ActivityType: [this.details.ActivityType, [Validators.required]],
            CourseType: [57, []],          // Validators set by Update Validators
            CollegeCreditType: [this.details.CollegeCreditType, [this.isCollegeCreditTypeRequiredValidator]],
            CECPerformanceStandard: [this.details.CECPerformanceStandard == 0 ? "" : this.details.CECPerformanceStandard,
            this.isParaApplicable ? [Validators.required] : []],
            EventName: [this.details.EventName, [Validators.required, Validators.maxLength(COURSE_ACTIVITY_NAME_MAX_LENGTH)]],
            EventStart: [this.details.EventStart.toString() != '0001-01-01T00:00:00' ? this.details.EventStart : null, [Validators.required]],
            EventEnd: [this.details.EventEnd.toString() != '0001-01-01T00:00:00' ? this.details.EventEnd : null, [Validators.required]],
            ShouldSubmitToPerms: [this.details.ShouldSubmitToPerms, []]
        });

        this.form.get('AUN').valueChanges.subscribe(() => {
            this.onAUNChange();
        });

        this.form.get('ActivityType').valueChanges.subscribe(() => {
            this.form.get('CollegeCreditType').updateValueAndValidity();
        });

        this.isPpidRequired = !this.details.IsPara;

        if (!this.canSave()) this.form.disable();

        this.updateValidators();
        this.form.get('PPID').valueChanges.subscribe(this.updateValidators);
        this.form.get('ShouldSubmitToPerms').valueChanges.subscribe(this.updateValidators);
    }

    onAUNChange() {
        const aun = this.form.get('AUN').value;
        const currentActivityType = this.form.get('ActivityType').value;
        const currentAUNSetting = this.aunSettings.find(aunSetting => aunSetting.AUN === aun);

        this.activityTypes = this.portalHelpersService.getValidActivityTypes(this.A48types, currentAUNSetting);

        if (this.activityTypes.length === 1) {
            this.form.get('ActivityType').setValue(this.activityTypes[0].id)
            this.form.get('ActivityType').updateValueAndValidity();
        } else if (!this.portalHelpersService.isValidActivityType(currentActivityType, currentAUNSetting)) {
            this.form.get('ActivityType').setValue(null)
            this.form.get('ActivityType').updateValueAndValidity();
        }
    }

    onSubmit({ value, valid }: { value: any, valid: boolean }, confirm: boolean = false) {

        this.busy = true;

        if (confirm) value.Confirm = confirm;
        this.act48Service.SaveUserSubmissionDetails(value)
            .subscribe((response: CplA48UserSubmissionDetail) => {
                this.busy = false;
                this.details = response;
                this.form.get('SubmissionID').setValue(response.SubmissionID);

                if (confirm) {
                    this.toastrService.success('Act 48 details confirmed successfully.', 'Confirm Successful');
                } else {
                    this.toastrService.success('Act 48 details saved successfully.', 'Save Successful');
                }

                this.onSubmissionComplete.emit({ success: true, details: response });

                if (confirm) this.activeModal.close();

            }, (error) => {
                this.busy = false;
                this.toastrService.error('An error occurred while saving Act 48 details. Please try again.', 'Save Failed');
                this.onSubmissionComplete.emit({ success: false, details: null });
            });
    }

    onResubmit({ value, valid }: { value: any, valid: boolean }) {
        this.busy = true;

        this.act48Service.ResubmitAct48UserSubmission(value)
            .subscribe((response: CplA48UserSubmissionDetail) => {
                this.busy = false;
                this.details = response;

                this.toastrService.success('Act 48 details resubmitted successfully.', 'Resubmission Successful');

                this.onSubmissionComplete.emit({ success: true, details: response });

                this.activeModal.close();

            }, (error) => {
                this.busy = false;
                this.toastrService.error('An error occurred while resubmitting Act 48 details. Please try again.', 'Resubmission Failed');
                this.onSubmissionComplete.emit({ success: false, details: null });
            });
    }

    private updateValidators = () => {
        const hasPPID = !!this.form.get('PPID').value;
        const ShouldSubmitToPerms = this.form.get('ShouldSubmitToPerms').value;
        const validators = ((!hasPPID || !ShouldSubmitToPerms) && this.details.IsPara) ? [] : [Validators.required]

        /*
        this.form.get('CourseType').setValidators(validators);
        this.form.get('CourseType').updateValueAndValidity();
        */
    }

    cancel() {
        this.activeModal.close();
    }

    shouldEnableSaveButton() {
        return this.canSave() && (!this.IsAdminView || this.form.valid);
    }

    canSave() {
        return this.details != null &&
            this.a48HelpersService
                .canSave({
                    StatusID: this.details.StatusID,
                    ParaStatusID: this.details.ParaStatusID,
                    isReadOnly: this.isReadOnly,
                    isItemComplete: this.isItemComplete
                });
    }

    showResubmitButton() {
        return this.details != null && this.a48HelpersService.canResubmit({
            StatusID: this.details.StatusID,
            ParaStatusID: this.details.ParaStatusID,
            isReadOnly: this.isReadOnly,
            IsAdminView: this.IsAdminView,
            isItemComplete: this.isItemComplete,
            ShouldSubmitToPerms: this.form.get('ShouldSubmitToPerms').value
        });
    }

    public canDisplaySubmitHoursToggle() {
        return this.a48HelpersService.canDisplaySubmitHoursToggle({
            hasAct48: true,
            isUserPara: this.isPara,
            hasPPID: !!this.form.get('PPID').value,
            excludeFromSubmission: this.user?.ExcludeFromSubmission,
            contextCode: this.details.ContextCode
        }) && this.AdminUI;
    }

    public ensureGreaterThanZero() {
        let cv = this.form.get("Hours").value;
        if (cv < 0) this.form.get("Hours").patchValue(0);
    }

    private isCollegeCreditTypeRequiredValidator = (formControl: AbstractControl) => {
        const parent = formControl.parent;
        if (this.isCollegeCreditTypeRequired(parent)) {
            return Validators.required(formControl);
        }

        return null;
    }

    public isCollegeCreditTypeRequired(act48Details: AbstractControl) {
        return act48Details && act48Details.get('ActivityType').value == A48ActivityType.CREDITS;
    }

}
